// Nadrama <https://nadrama.com>
// Copyright Nadrama Pty Ltd
// All rights reserved.

export type PublicJwk = JsonWebKey & { crv: "P-256"; kty: "EC"; x: string; y: string };

const base64url = (value: ArrayBuffer | Uint8Array | string): string => {
  const bytes = typeof value === "string" ? new TextEncoder().encode(value) : new Uint8Array(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
};

export const publicJwk = async (key: CryptoKey): Promise<PublicJwk> => {
  const jwk = await crypto.subtle.exportKey("jwk", key);
  if (jwk.kty !== "EC" || jwk.crv !== "P-256" || !jwk.x || !jwk.y) throw new Error("The browser returned an invalid DPoP key.");
  return { kty: "EC", crv: "P-256", x: jwk.x, y: jwk.y };
};

export const thumbprint = async (jwk: PublicJwk): Promise<string> =>
  base64url(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify({ crv: "P-256", kty: "EC", x: jwk.x, y: jwk.y }))));

const rawSignature = (signature: ArrayBuffer): Uint8Array => {
  const bytes = new Uint8Array(signature);
  if (bytes.length === 64) return bytes;
  if (bytes[0] !== 0x30) throw new Error("The browser returned an invalid DPoP signature.");
  let offset = bytes[1]! & 0x80 ? 2 + (bytes[1]! & 0x7f) : 2;
  const readInteger = (): Uint8Array => {
    if (bytes[offset++] !== 0x02) throw new Error("The browser returned an invalid DPoP signature.");
    const length = bytes[offset++]!;
    let integer = bytes.slice(offset, offset + length);
    offset += length;
    while (integer.length > 32 && integer[0] === 0) integer = integer.slice(1);
    if (integer.length > 32) throw new Error("The browser returned an invalid DPoP signature.");
    const padded = new Uint8Array(32);
    padded.set(integer, 32 - integer.length);
    return padded;
  };
  const raw = new Uint8Array(64);
  raw.set(readInteger());
  raw.set(readInteger(), 32);
  return raw;
};

export const createProof = async (privateKey: CryptoKey, jwk: PublicJwk, method: string, url: string, ath?: string): Promise<string> => {
  const header = base64url(JSON.stringify({ alg: "ES256", jwk, typ: "dpop+jwt" }));
  const payload = base64url(JSON.stringify({ htm: method.toUpperCase(), htu: new URL(url).origin + new URL(url).pathname, iat: Math.floor(Date.now() / 1000), jti: crypto.randomUUID?.() ?? base64url(crypto.getRandomValues(new Uint8Array(16))), ...(ath ? { ath } : {}) }));
  const signingInput = `${header}.${payload}`;
  const signature = await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, privateKey, new TextEncoder().encode(signingInput));
  return `${signingInput}.${base64url(rawSignature(signature))}`;
};
